import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  DialogContentText,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
  Fade,
  Zoom,
  Divider
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Warning as WarningIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as LocalShippingIcon,
  Receipt as ReceiptIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { api } from '../../utils/api';
import { getProductImage as getProductImageUtil } from '../../utils/productUtils';

const CartTab = () => {
  const [cart, setCart] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [openMissingDataDialog, setOpenMissingDataDialog] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const [totals, setTotals] = useState({
    subtotal: 0,
    vat: 0,
    total: 0
  });
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [orderStep, setOrderStep] = useState(0);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [addressData, setAddressData] = useState(null);

  useEffect(() => {
    // Assicurati che il company ID sia impostato negli headers se disponibile nel localStorage
    const localCompanyId = localStorage.getItem('companyId') || localStorage.getItem('selectedCompanyId');
    if (localCompanyId && !api.defaults.headers['x-company-id']) {
      console.log('🏢 Impostazione company ID da localStorage:', localCompanyId);
      api.defaults.headers['x-company-id'] = localCompanyId;
    }
    
    loadCart();
    loadAddressData();
  }, []);

  // Ricarica il carrello se cambia il company ID
  useEffect(() => {
    const companyId = api.defaults.headers['x-company-id'] || localStorage.getItem('companyId');
    if (companyId) {
      console.log('🔄 Company ID rilevato, ricarico il carrello:', companyId);
      loadCart();
    }
  }, [api.defaults.headers['x-company-id']]);

  // Ascolta i cambiamenti nello storage (per sincronizzazione tra tab)
  useEffect(() => {
    const handleStorageChange = (e) => {
      const companyId = api.defaults.headers['x-company-id'] || localStorage.getItem('companyId');
      if (companyId && e.key === `shopCart_${companyId}`) {
        console.log('🔄 Rilevata modifica al carrello in altra tab, ricarico');
        loadCart();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    calculateTotals();
  }, [cart]);

  const loadCart = () => {
    try {
      // Ottieni l'ID azienda con fallback al localStorage
      const companyId = api.defaults.headers['x-company-id'] || localStorage.getItem('companyId');
      console.log('🛒 Tentativo di caricamento carrello per azienda:', companyId);
      
      if (!companyId) {
        console.error('ID azienda mancante, impossibile caricare il carrello');
        setCart([]);
        return;
      }
      
      const savedCart = localStorage.getItem(`shopCart_${companyId}`);
      console.log('🛒 Carrello salvato trovato:', savedCart ? 'Sì' : 'No');
      
      if (!savedCart) {
        // Nessun carrello salvato, inizializza con array vuoto
        console.log('🛒 Nessun carrello trovato nel localStorage');
        setCart([]);
        return;
      }
      
      try {
        const cartData = JSON.parse(savedCart);
        console.log('🛒 Dati carrello nel localStorage:', cartData);
        
        // Verifica che cartData sia un array
        if (!Array.isArray(cartData)) {
          console.error('Formato carrello non valido, reimpostazione carrello');
          localStorage.removeItem(`shopCart_${companyId}`);
          setCart([]);
          return;
        }
        
        // Filtra solo i prodotti dell'azienda corrente o quelli senza companyId
        const filteredCart = cartData.filter(item => 
          item && typeof item === 'object' && 
          (item.companyId === companyId || !item.companyId)
        );
        
        // Assicurati che tutti gli elementi abbiano il companyId corretto
        filteredCart.forEach(item => {
          item.companyId = companyId;
        });
        
        console.log('🛒 Carrello filtrato per azienda:', filteredCart);
        
        if (filteredCart.length === 0) {
          console.log('🛒 Nessun prodotto trovato per l\'azienda corrente');
        }
        
        setCart(filteredCart);
        
        // Resalva il carrello con gli ID azienda corretti
        if (filteredCart.length > 0) {
          localStorage.setItem(`shopCart_${companyId}`, JSON.stringify(filteredCart));
        }
      } catch (parseError) {
        console.error('Errore nel parsing del carrello:', parseError);
        // In caso di errore di parsing, resetta il carrello
        localStorage.removeItem(`shopCart_${companyId}`);
        setCart([]);
      }
    } catch (err) {
      console.error('Errore nel caricamento del carrello:', err);
      setCart([]);
    }
  };

  const loadAddressData = async () => {
    try {
      const response = await api.get('/settings');
      const settings = response.data;
      
      setAddressData({
        billingAddress: {
          name: settings.companyName,
          address: settings.companyAddress,
          city: settings.companyCity,
          postalCode: settings.companyPostalCode,
          province: settings.companyProvince,
          vatNumber: settings.companyVatNumber
        },
        shippingAddress: {
          name: settings.shippingName,
          address: settings.shippingAddress,
          city: settings.shippingCity,
          postalCode: settings.shippingPostalCode,
          province: settings.shippingProvince,
          phone: settings.shippingPhone
        }
      });
    } catch (err) {
      console.error('Errore nel caricamento degli indirizzi:', err);
    }
  };

  const calculateTotals = () => {
    const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const vat = subtotal * 0.22;
    setTotals({
      subtotal,
      vat,
      total: subtotal + vat
    });
  };

  const handleQuantityChange = (index, increment) => {
    const companyId = api.defaults.headers['x-company-id'] || localStorage.getItem('companyId');
    console.log('🛒 Modifica quantità per azienda:', companyId);
    
    if (!companyId) {
      console.error('ID azienda mancante, impossibile modificare quantità');
      return;
    }
    
    const newCart = [...cart];
    const item = newCart[index];
    const minQty = item.minStock || 1;

    // Calcola la nuova quantità come multiplo della quantità minima
    let newQuantity;
    if (increment) {
      newQuantity = item.quantity + minQty;
    } else {
      // Quando decrementiamo, assicuriamoci di non scendere sotto minQty
      newQuantity = Math.max(minQty, item.quantity - minQty);
    }

    // Assicurati che la quantità sia sempre un multiplo della quantità minima
    newQuantity = Math.ceil(newQuantity / minQty) * minQty;

    newCart[index] = { ...item, quantity: newQuantity, companyId };
    localStorage.setItem(`shopCart_${companyId}`, JSON.stringify(newCart));
    console.log('🛒 Carrello aggiornato dopo modifica quantità:', newCart);
    setCart(newCart);
  };

  const handleRemoveItem = (index) => {
    const companyId = api.defaults.headers['x-company-id'] || localStorage.getItem('companyId');
    console.log('🛒 Rimuovo articolo dal carrello per azienda:', companyId);
    
    if (!companyId) {
      console.error('ID azienda mancante, impossibile rimuovere articolo');
      return;
    }
    
    const newCart = cart.filter((_, i) => i !== index);
    localStorage.setItem(`shopCart_${companyId}`, JSON.stringify(newCart));
    console.log('🛒 Carrello aggiornato dopo rimozione:', newCart);
    setCart(newCart);
  };

  const handleOpenProductDialog = (product) => {
    const processedProduct = {
      ...product,
      image: typeof product.image === 'object' ? product.image.text || product.image.hyperlink || product.image.value || '' : product.image
    };
    setSelectedProduct(processedProduct);
    setOpenProductDialog(true);
  };

  const handleCloseProductDialog = () => {
    setOpenProductDialog(false);
    setSelectedProduct(null);
  };

  // Function to get default product image if none is available
  const getProductImage = (product) => {
    try {
      // Gestione speciale per E-WHEEL se non c'è immagine
      if (product && (product.brand === 'E-WHEEL' || product.brand === 'EWHEEL') && !product.image) {
        const altCode = product.altCode || product.code || '';
        const codeNumbers = altCode.replace(/\D/g, '');
        if (codeNumbers) {
          return `https://ewheel.es/${codeNumbers}-large_default/${codeNumbers}.jpg`;
        }
      }
      
      // Usa la funzione utility che gestisce già URL multipli (prende solo la prima)
      return getProductImageUtil(product?.image);
    } catch (err) {
      console.error('Errore nel caricamento dell\'immagine:', err);
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNFRUVFRUUiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzY2NiIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0Ij5JbW1hZ2luZSBub24gZGlzcG9uaWJpbGU8L3RleHQ+PC9zdmc+';
    }
  };

  const validateOrderData = async () => {
    try {
      const response = await api.get('/settings');
      const settings = response.data; // I dati sono già un oggetto, non serve reduce

      const requiredFields = [
        { key: 'companyName', label: 'Nome Azienda' },
        { key: 'companyAddress', label: 'Indirizzo Fatturazione' },
        { key: 'companyCity', label: 'Città Fatturazione' },
        { key: 'companyPostalCode', label: 'CAP Fatturazione' },
        { key: 'companyProvince', label: 'Provincia Fatturazione' },
        { key: 'companyVatNumber', label: 'P.IVA' },
        { key: 'shippingName', label: 'Nome Spedizione' },
        { key: 'shippingAddress', label: 'Indirizzo Spedizione' },
        { key: 'shippingCity', label: 'Città Spedizione' },
        { key: 'shippingPostalCode', label: 'CAP Spedizione' },
        { key: 'shippingProvince', label: 'Provincia Spedizione' },
        { key: 'shippingPhone', label: 'Telefono Spedizione' }
      ];

      const missing = requiredFields.filter(field => !settings[field.key]);
      
      if (missing.length > 0) {
        setMissingFields(missing);
        setOpenMissingDataDialog(true);
        return false;
      }

      return true;
    } catch (err) {
      console.error('Errore nella validazione dei dati:', err);
      setSnackbar({
        open: true,
        message: 'Errore nella validazione dei dati. Riprova più tardi.',
        severity: 'error'
      });
      return false;
    }
  };

  const handleOpenConfirmDialog = async () => {
    const isValid = await validateOrderData();
    if (!isValid) return;
    setOpenConfirmDialog(true);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setOrderStep(0);
    setOrderSuccess(false);
  };

  const handlePlaceOrder = async () => {
    try {
      setLoading(true);
      
      // Step 1: Verifica ordine
      setOrderStep(1);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Step 2: Elaborazione
      setOrderStep(2);
      
      // Prepara gli articoli dell'ordine
      const orderItems = cart.map(item => {
        // Mantieni TUTTE le immagini (non solo la prima) per salvarle nell'ordine
        // così quando arriva in "articoli da ricevere" e viene creato nel magazzino,
        // avrà tutte le immagini disponibili
        let imageUrl = item.image;
        
        // Se è un oggetto, estrai l'URL
        if (imageUrl && typeof imageUrl === 'object') {
          imageUrl = imageUrl.text || imageUrl.hyperlink || imageUrl.value || imageUrl.url || '';
        }
        
        // Converti imageUrl a stringa o null, mantenendo TUTTE le immagini
        if (imageUrl && typeof imageUrl !== 'string') {
          imageUrl = String(imageUrl).trim() || null;
        }
        
        // Se è una stringa vuota o null, imposta a null
        if (!imageUrl || imageUrl === 'null' || imageUrl === 'undefined') {
          imageUrl = null;
        }
        
        return {
          code: item.code,
          description: item.name,
          quantity: item.quantity,
          price: item.price,
          suggestedPrice: item.suggestedPrice || 0,
          total: item.price * item.quantity,
          image: imageUrl, // Salva TUTTE le immagini (separate da ;)
          companyId: api.defaults.headers['x-company-id']
        };
      });

      // Step 3: Invio
      setOrderStep(3);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Prepara il payload dell'ordine
      const orderPayload = {
        items: orderItems,
        ...addressData
      };
      
      console.log('📦 Payload ordine da inviare:', JSON.stringify(orderPayload, null, 2));
      
      // Crea l'ordine
      const response = await api.post('/orders', orderPayload);

      // Pulisci il carrello
      localStorage.removeItem(`shopCart_${api.defaults.headers['x-company-id']}`);
      setCart([]);
      calculateTotals();

      // Step 4: Completato
      setOrderStep(4);
      
      // Mostra il messaggio di successo dopo un breve ritardo
      setTimeout(() => {
        setOrderSuccess(true);
        setLoading(false);
      }, 500);

    } catch (error) {
      console.error('Errore durante il checkout:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Errore durante il checkout. Riprova più tardi.';
      console.error('Dettagli errore:', error.response?.data);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      setLoading(false);
      setOrderStep(0);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCloseMissingDataDialog = () => {
    setOpenMissingDataDialog(false);
    setMissingFields([]);
  };

  return (
    <Box>
      {/* Riepilogo totali */}
      <Paper sx={{ p: 2, mb: 3 }} elevation={2}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1" color="textSecondary">
              Imponibile
            </Typography>
            <Typography variant="h5">
              €{totals && totals.subtotal !== undefined ? totals.subtotal.toFixed(2) : '0.00'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1" color="textSecondary">
              IVA (22%)
            </Typography>
            <Typography variant="h5">
              €{totals && totals.vat !== undefined ? totals.vat.toFixed(2) : '0.00'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1" color="textSecondary">
              Totale (IVA inclusa)
            </Typography>
            <Typography variant="h5" color="primary">
              €{totals && totals.total !== undefined ? totals.total.toFixed(2) : '0.00'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabella prodotti */}
      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Codice</TableCell>
              <TableCell>Descrizione</TableCell>
              <TableCell align="right">Quantità</TableCell>
              <TableCell align="right">Prezzo</TableCell>
              <TableCell align="right">Totale</TableCell>
              <TableCell align="center">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cart.map((item, index) => (
              <TableRow 
                key={item.code}
                hover
                onClick={() => handleOpenProductDialog(item)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{item.code}</TableCell>
                <TableCell>{item.name}</TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleQuantityChange(index, false);
                      }}
                      disabled={item.quantity <= (item.minStock || 1)}
                    >
                      <RemoveIcon />
                    </IconButton>
                    <Typography>
                      {item.quantity}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleQuantityChange(index, true);
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell align="right">€{item.price !== undefined && item.price !== null ? item.price.toFixed(2) : '0.00'}</TableCell>
                <TableCell align="right">€{item.price !== undefined && item.price !== null ? (item.price * item.quantity).toFixed(2) : '0.00'}</TableCell>
                <TableCell align="center">
                  <IconButton
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveItem(index);
                    }}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {cart.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body1" sx={{ py: 3 }}>
                    Il carrello è vuoto
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pulsanti azione */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleOpenConfirmDialog}
          disabled={cart.length === 0}
          startIcon={<SendIcon />}
        >
          Procedi all'ordine
        </Button>
      </Box>

      {/* Dialog dettaglio prodotto */}
      <Dialog
        open={openProductDialog}
        onClose={handleCloseProductDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="subtitle2" color="textSecondary">
            Codice: {selectedProduct?.code}
          </Typography>
          <Typography variant="h6" sx={{ mt: 1 }}>
            {selectedProduct?.name}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selectedProduct && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <CardMedia
                  component="img"
                  image={getProductImage(selectedProduct)}
                  alt={selectedProduct.name}
                  sx={{ height: 300, objectFit: 'contain', bgcolor: '#f5f5f5', borderRadius: 1 }}
                  onError={(e) => {
                    console.warn(`Errore nel caricamento dell'immagine per prodotto ${selectedProduct.id}`);
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNFRUVFRUUiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzY2NiIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0Ij5JbW1hZ2luZSBub24gZGlzcG9uaWJpbGU8L3RleHQ+PC9zdmc+';
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="body1" paragraph>
                      {selectedProduct.name}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Quantità
                        </Typography>
                        <Typography variant="body1">
                          {selectedProduct.quantity}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Prezzo unitario
                        </Typography>
                        <Typography variant="body1">
                          €{selectedProduct.price !== undefined && selectedProduct.price !== null ? selectedProduct.price.toFixed(2) : '0.00'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Totale
                        </Typography>
                        <Typography variant="h6" color="primary">
                          €{selectedProduct.price !== undefined && selectedProduct.price !== null ? (selectedProduct.price * selectedProduct.quantity).toFixed(2) : '0.00'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProductDialog}>
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog dati mancanti */}
      <Dialog
        open={openMissingDataDialog}
        onClose={handleCloseMissingDataDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            <Typography>Dati Mancanti</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Per procedere con l'ordine, è necessario completare i seguenti dati nelle impostazioni:
          </DialogContentText>
          <List>
            {missingFields.map((field, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <SettingsIcon color="action" />
                </ListItemIcon>
                <ListItemText primary={field.label} />
              </ListItem>
            ))}
          </List>
          <DialogContentText sx={{ mt: 2 }}>
            Per favore, completa i dati mancanti nelle impostazioni e riprova.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMissingDataDialog}>
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog di conferma ordine */}
      <Dialog
        open={openConfirmDialog}
        onClose={loading ? undefined : handleCloseConfirmDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">
            Conferma Ordine
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={orderStep} sx={{ mb: 4 }}>
            <Step>
              <StepLabel>Verifica Ordine</StepLabel>
            </Step>
            <Step>
              <StepLabel>Elaborazione</StepLabel>
            </Step>
            <Step>
              <StepLabel>Invio</StepLabel>
            </Step>
            <Step>
              <StepLabel>Completato</StepLabel>
            </Step>
          </Stepper>

          {orderSuccess ? (
            <Zoom in={orderSuccess}>
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Ordine Inviato con Successo!
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  Riceverai una conferma via email con i dettagli dell'ordine.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCloseConfirmDialog}
                >
                  OK
                </Button>
              </Box>
            </Zoom>
          ) : (
            <>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Riepilogo Ordine
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <List dense>
                      {cart.map((item, index) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={
                              <Typography variant="body1" sx={{ color: '#000' }}>
                                {item.name}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {`${item.quantity}x €${item.price !== undefined && item.price !== null ? item.price.toFixed(2) : '0.00'}`}
                              </Typography>
                            }
                          />
                          <Typography variant="body2" sx={{ color: '#000' }}>
                            €{item.price !== undefined && item.price !== null ? (item.price * item.quantity).toFixed(2) : '0.00'}
                          </Typography>
                        </ListItem>
                      ))}
                    </List>
                    <Divider sx={{ my: 2 }} />
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Imponibile:
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sx={{ textAlign: 'right' }}>
                        <Typography variant="body2">
                          €{totals && totals.subtotal !== undefined ? totals.subtotal.toFixed(2) : '0.00'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          IVA (22%):
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sx={{ textAlign: 'right' }}>
                        <Typography variant="body2">
                          €{totals && totals.vat !== undefined ? totals.vat.toFixed(2) : '0.00'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle1">
                          Totale:
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sx={{ textAlign: 'right' }}>
                        <Typography variant="subtitle1" color="primary">
                          €{totals && totals.total !== undefined ? totals.total.toFixed(2) : '0.00'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Informazioni di Spedizione
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <LocalShippingIcon sx={{ mr: 1 }} color="primary" />
                      <Typography variant="subtitle1">
                        Spese di Spedizione
                      </Typography>
                    </Box>
                    <Typography variant="body2" paragraph>
                      Il costo delle spese di spedizione sarà calcolato successivamente
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <ReceiptIcon sx={{ mr: 1 }} color="primary" />
                      <Typography variant="subtitle1">
                        Fatturazione
                      </Typography>
                    </Box>
                    <Typography variant="body2">
                      Riceverai la fattura via email
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                {loading ? (
                  <CircularProgress />
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handlePlaceOrder}
                    startIcon={<SendIcon />}
                    size="large"
                  >
                    Conferma e Invia Ordine
                  </Button>
                )}
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar per le notifiche */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CartTab; 